import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Pagination } from "@/organism";
import { TaskFilter } from "@/organism/BossBaby";
import { BossBabyTaskDto, BossBabySearchParams } from "@/dto/BossBabyDto";
import { format } from "date-fns";

interface TaskListTemplateProps {
  tasks: BossBabyTaskDto[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSearch: (params: BossBabySearchParams) => void;
  onEdit: (taskId: number) => void;
  onDelete: (taskId: number) => void;
  onCreate: () => void;
}

export const TaskListTemplate: React.FC<TaskListTemplateProps> = ({
  tasks,
  loading,
  total,
  page,
  limit,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onEdit,
  onDelete,
  onCreate,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [isActive, setIsActive] = useState<boolean | "">("");
  const [priorityLevel, setPriorityLevel] = useState("");
  const [executionFrequency, setExecutionFrequency] = useState("");
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSearch = useCallback(() => {
    const params: BossBabySearchParams = {
      search: searchTerm.trim() || undefined,
      category: category || undefined,
      isActive: isActive === "" ? undefined : isActive,
      sortBy: sortBy as "name" | "createdAt" | "priority",
      sortOrder,
    };
    onSearch(params);
  }, [searchTerm, category, isActive, sortBy, sortOrder, onSearch]);

  const handleReset = useCallback(() => {
    setSearchTerm("");
    setCategory("");
    setIsActive("");
    setPriorityLevel("");
    setExecutionFrequency("");
    setSortBy("name");
    setSortOrder("asc");
    onSearch({});
  }, [onSearch]);

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
            <CircularProgress size={30} />
          </TableCell>
        </TableRow>
      );
    }

    if (tasks.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <Typography variant="h6" color="text.secondary">
                No Boss Baby Tasks found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first Boss Baby Task to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreate}
                size="small"
              >
                Create Task
              </Button>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return tasks.map((task) => (
      <TableRow key={task.taskId} hover>
        <TableCell align="right">{task.taskId}</TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {task.taskName}
          </Typography>
          {task.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {task.description}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip label={task.category} size="small" />
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {task.tools && task.tools.length > 0 ? (
              task.tools
                .slice(0, 3)
                .map((tool, index) => (
                  <Chip
                    key={index}
                    label={tool}
                    size="small"
                    variant="outlined"
                  />
                ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                -
              </Typography>
            )}
            {task.tools && task.tools.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{task.tools.length - 3} more
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={task.priorityLevel}
            size="small"
            color={
              task.priorityLevel === "HIGH"
                ? "error"
                : task.priorityLevel === "MEDIUM"
                ? "warning"
                : "default"
            }
          />
        </TableCell>
        <TableCell align="center">
          <Chip label={task.executionFrequency} size="small" />
        </TableCell>
        <TableCell align="center">
          <Chip
            label={task.isActive ? "Active" : "Inactive"}
            size="small"
            color={task.isActive ? "success" : "default"}
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(task.createdAt), "yyyy-MM-dd")}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => onEdit(task.taskId)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(task.taskId)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Boss Baby Tasks
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage Boss Baby tasks and their execution workflows
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
          size="large"
        >
          Create Task
        </Button>
      </Box>

      {/* Search & Filter */}
      <TaskFilter
        searchTerm={searchTerm}
        category={category}
        isActive={isActive}
        priorityLevel={priorityLevel}
        executionFrequency={executionFrequency}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearchTermChange={setSearchTerm}
        onCategoryChange={setCategory}
        onIsActiveChange={setIsActive}
        onPriorityLevelChange={setPriorityLevel}
        onExecutionFrequencyChange={setExecutionFrequency}
        onSortByChange={setSortBy}
        onSortOrderChange={setSortOrder}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {loading ? "Loading..." : `Showing ${tasks.length} of ${total} tasks`}
        </Typography>
      </Box>

      {/* Task Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="right" sx={{ width: 80 }}>
                ID
              </TableCell>
              <TableCell sx={{ minWidth: 200 }}>Task Name</TableCell>
              <TableCell sx={{ width: 120 }}>Category</TableCell>
              <TableCell sx={{ width: 200 }}>Tools</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Priority
              </TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Frequency
              </TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Status
              </TableCell>
              <TableCell sx={{ width: 120 }}>Created</TableCell>
              <TableCell align="center" sx={{ width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableContent()}</TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!loading && tasks.length > 0 && (
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={total}
            page={page - 1}
            rowsPerPage={limit}
            onPageChange={(_, newPage) => onPageChange(newPage + 1)}
            onRowsPerPageChange={(e) =>
              onRowsPerPageChange(parseInt(e.target.value, 10))
            }
          />
        </Box>
      )}
    </Container>
  );
};
